import { useState } from "react";
import { useAction, useQuery } from "convex/react";
import { ExternalLink } from "lucide-react";
import { api } from "../../lib/convexApi";
import { useEntitlements } from "../../hooks/useEntitlements";
import { getPlanMeta } from "../../api/billing";

/**
 * PlanBillingTab — current plan display + Lemon Squeezy portal/upgrade.
 *
 * Plan source: the reactive Convex `myEntitlements` query (via
 * useEntitlements), the same source requireOrg enforces — so the tab and
 * the backend can never disagree. The subscription_updated webhook flips
 * the tier and this tab re-renders reactively (no manual refresh). Spec 7
 * replaces the old Clerk `has({plan})` probe + getBillingPortalUrl env hook.
 */
export function PlanBillingTab() {
  const ent = useEntitlements();
  const portalUrl = useAction(api.billing.checkout.portalUrl);
  const changeTier = useAction(api.billing.checkout.changeTier);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Current tier = highest entitled. hasSecurity → "security", else base.
  const slug =
    ent.kind === "ready" ? (ent.hasSecurity ? "security" : ent.hasPro ? "pro" : "base") : null;
  // Billing cadence drives which price string we show. Reactive: derived from
  // the persisted LS variant id, so an annual subscriber sees "$192/yr"
  // rather than the monthly default (Codex). Undefined while loading / no sub
  // → getPlanMeta falls back to "monthly".
  const sub = useQuery(api.billing.queries.mySubscription);
  const plan = getPlanMeta(slug, sub?.period ?? "monthly");

  async function handleManage() {
    setBusy(true);
    setErr(null);
    // Open the tab synchronously inside the click gesture, then navigate it
    // once the signed portal URL resolves. Opening AFTER the await trips popup
    // blockers (the gesture is gone). Note: window.open(..., "noopener") returns
    // null — the browser withholds the handle — so we open without it and sever
    // the opener ourselves to keep the portal page from reaching window.opener.
    const tab = window.open("about:blank", "_blank");
    if (!tab) {
      // Popup blocked → bail before portalUrl so we don't mint an unused
      // Lemon Squeezy portal URL, and name the actual cause.
      setErr("Could not open the billing portal — please allow popups for this site.");
      setBusy(false);
      return;
    }
    tab.opener = null;
    try {
      const res = await portalUrl({});
      if (res.ok) {
        tab.location.href = res.url;
      } else {
        tab.close();
        setErr("Could not open the billing portal.");
      }
    } catch {
      tab.close();
      setErr("Could not open the billing portal.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSwitch(tier: "base" | "pro" | "security") {
    setBusy(true);
    setErr(null);
    try {
      await changeTier({ tier });
      // The subscription_updated webhook flips the tier; useEntitlements
      // re-renders this tab reactively. No manual refresh needed.
    } catch {
      setErr("Could not change your plan. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ac-plan-billing">
      <h2 className="ac-section-title">Plan &amp; billing</h2>
      <p className="ac-section-subtitle">
        Per-seat pricing. Switch tiers any time — proration applies.
      </p>

      {plan === null ? (
        <div className="ac-plan-card ac-plan-card--empty">
          <p className="ac-plan-card__title">
            {ent.kind === "loading" ? "Loading…" : "No active plan"}
          </p>
          {ent.kind !== "loading" && (
            <p className="ac-plan-card__meta">Subscribe to a plan to unlock Steerly features.</p>
          )}
        </div>
      ) : (
        <div className="ac-plan-card">
          <div className="ac-plan-card__header">
            <span className="ac-plan-card__title">{plan.name}</span>
            <span className="ac-plan-card__price">{plan.price}</span>
          </div>
          <div className="ac-plan-card__actions">
            <button type="button" className="ac-plan-cta" disabled={busy} onClick={handleManage}>
              Manage subscription <ExternalLink size={13} aria-hidden />
            </button>
            {slug === "base" && (
              <>
                <button
                  type="button"
                  className="ac-plan-cta ac-plan-cta--secondary"
                  disabled={busy}
                  onClick={() => handleSwitch("pro")}
                >
                  Upgrade to Pro
                </button>
                <button
                  type="button"
                  className="ac-plan-cta ac-plan-cta--secondary"
                  disabled={busy}
                  onClick={() => handleSwitch("security")}
                >
                  Upgrade to Ultra Security
                </button>
              </>
            )}
            {slug === "pro" && (
              <>
                <button
                  type="button"
                  className="ac-plan-cta ac-plan-cta--secondary"
                  disabled={busy}
                  onClick={() => handleSwitch("security")}
                >
                  Upgrade to Ultra Security
                </button>
                <button
                  type="button"
                  className="ac-plan-cta ac-plan-cta--secondary"
                  disabled={busy}
                  onClick={() => handleSwitch("base")}
                >
                  Downgrade to Base
                </button>
              </>
            )}
            {slug === "security" && (
              <>
                <button
                  type="button"
                  className="ac-plan-cta ac-plan-cta--secondary"
                  disabled={busy}
                  onClick={() => handleSwitch("pro")}
                >
                  Downgrade to Pro
                </button>
                <button
                  type="button"
                  className="ac-plan-cta ac-plan-cta--secondary"
                  disabled={busy}
                  onClick={() => handleSwitch("base")}
                >
                  Downgrade to Base
                </button>
              </>
            )}
          </div>
          {err && (
            <p className="ac-plan-card__error" role="alert">
              {err}
            </p>
          )}
        </div>
      )}

      <div className="ac-plan-enterprise">
        <a
          href="https://steerly.ai/contact-enterprise"
          target="_blank"
          rel="noopener noreferrer"
          className="ac-plan-enterprise__link"
        >
          Enterprise pricing — SSO, custom packs, audit retention, on-prem
          <ExternalLink size={12} aria-hidden />
        </a>
      </div>
    </div>
  );
}
